/**
 * @fileoverview TourDisplayArea.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React, { useState, useMemo, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import TourFeatureCard from './TourFeatureCard'
import TourFilter from './TourFilter'
import { apiFetch } from '../../utils/api'
import SEO from '../ui/SEO'

function SkeletonCard() {
  return (
    <div className="w-[340px] md:w-[380px] h-[480px] bg-white rounded-[32px] overflow-hidden flex flex-col shadow-sm border border-gray-100 animate-pulse shrink-0 snap-center relative group p-1">
      <div className="w-full h-1/2 bg-gray-200 rounded-[28px]"></div>
      <div className="flex-1 p-6 flex flex-col gap-3 relative">
        <div className="h-6 w-3/4 bg-gray-200 rounded"></div>
        <div className="h-4 w-1/2 bg-gray-200 rounded"></div>
        <div className="h-10 w-full bg-gray-200 rounded-lg mt-auto"></div>
      </div>
    </div>
  )
}

function TourDisplayArea() {
  const { t } = useTranslation();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedDuration, setSelectedDuration] = useState('All');
  const [selectedPrice, setSelectedPrice] = useState('All');
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const listRef = useRef(null);

  useEffect(() => {
    apiFetch('/tours?status=active')
      .then(res => {
        if (res && res.data && res.data.length > 0) {
          const formatted = res.data.map(t => ({
            id: t._id,
            name: t.title,
            slug: t.slug || t.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, ''),
            category: t.categories?.[0] || 'Tour',
            duration: `${t.duration} Days | ${t.duration - 1} Nights`,
            durationDays: t.duration,
            price: t.price,
            description: t.description || '',
            features: t.includes && t.includes.length > 0 ? t.includes.slice(0, 4) : ['hotel', 'meal', 'car', 'guide'],
            featuredIcons: t.featuredIcons || [],
            image: (t.image && typeof t.image === 'object' && (t.image.l || t.image.h)) 
              ? { l: t.image.l || t.image.h, h: t.image.h || t.image.l }
              : { l: (t.images && t.images[0]) || t.image || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800', h: (t.images && t.images[0]) || t.image || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800' }
          })).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
          setTours(formatted);
        } else {
          setTours([]);
        }
      })
      .catch(() => {
        setTours([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const filteredTours = useMemo(() => {
    return tours.filter(tour => {
      const categoryMatch = selectedCategory === 'All' || (tour.category && tour.category.trim().toLowerCase() === selectedCategory.trim().toLowerCase());
      
      let durationMatch = true;
      if (selectedDuration !== 'All') {
        const days = tour.durationDays;
        if (selectedDuration === '1-3') durationMatch = days >= 1 && days <= 3;
        else if (selectedDuration === '4-7') durationMatch = days >= 4 && days <= 7;
        else if (selectedDuration === '8+') durationMatch = days >= 8;
      }

      let priceMatch = true;
      if (selectedPrice !== 'All') {
        const price = tour.price;
        if (selectedPrice === '0-1000') priceMatch = price < 1000;
        else if (selectedPrice === '1000-2500') priceMatch = price >= 1000 && price <= 2500;
        else if (selectedPrice === '2500+') priceMatch = price > 2500;
      }
      
      return categoryMatch && durationMatch && priceMatch;
    });
  }, [tours, selectedCategory, selectedDuration, selectedPrice]);

  const totalPages = Math.ceil(filteredTours.length / itemsPerPage);
  
  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredTours.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredTours, currentPage]);

  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  }

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (listRef.current) {
        listRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className='flex flex-col w-full gap-10 items-center'>
        {tours.length > 0 && (
          <SEO 
              title="Tour Plans" 
              description="Browse curated Sri Lanka tour plans designed by local experts. From adventure cycling and cultural heritage journeys to beach retreats and wildlife safaris, find the holiday package that suits your travel style and budget."
              keywords="Sri Lanka tour packages, Sri Lanka holiday plans, adventure tours Sri Lanka, cultural tours Sri Lanka, beach holidays Sri Lanka, wildlife safari packages, custom itinerary Sri Lanka"
              schema={{
                "@context": "https://schema.org",
                "@type": "ItemList",
                "name": "Sri Lanka Tour Packages",
                "description": "Curated Sri Lanka holiday tour packages offered by KITH Travels.",
                "numberOfItems": tours.length,
                "itemListElement": tours.map((tour, idx) => ({
                  "@type": "ListItem",
                  "position": idx + 1,
                  "item": {
                    "@type": "TouristTrip",
                    "name": tour.name,
                    "description": tour.description,
                    "offers": {
                      "@type": "Offer",
                      "price": tour.price,
                      "priceCurrency": "USD"
                    }
                  }
                }))
              }}
          />
        )}
        <TourFilter 
            selectedCategory={selectedCategory} 
            setSelectedCategory={handleFilterChange(setSelectedCategory)}
            selectedDuration={selectedDuration}
            setSelectedDuration={handleFilterChange(setSelectedDuration)}
            selectedPrice={selectedPrice}
            setSelectedPrice={handleFilterChange(setSelectedPrice)}
        />

        <div ref={listRef} className='w-full flex flex-col items-center md:items-start min-h-[300px] justify-center'>
            {loading ? (
                <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 w-full justify-items-center md:justify-items-start'>
                    {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
                </div>
            ) : tours.length === 0 ? (
                <div className='py-20 text-center w-full'>
                    <p className='text-gray-500 text-lg font-medium'>{t('tours.no_tours_avail')}</p>
                    <Link to="/custom-tour" className="inline-block mt-4 px-6 py-2 bg-KITH-primary text-white rounded-full font-medium hover:bg-opacity-90 transition-all">
                        {t('tours.plan_own_trip')}
                    </Link>
                </div>
            ) : currentItems.length > 0 ? (
                <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 w-full justify-items-center md:justify-items-start'>
                    {currentItems.map(tour => (
                        <TourFeatureCard key={tour.id} tour={tour} />
                    ))}
                </div>
            ) : (
                <div className='py-20 text-center w-full'>
                    <p className='text-gray-500 text-lg font-medium'>No tours found matching your filters.</p>
                    <button 
                        onClick={() => { setSelectedCategory('All'); setSelectedDuration('All'); setSelectedPrice('All'); setCurrentPage(1); }}
                        className='mt-4 text-KITH-primary font-bold hover:underline'
                    >
                        Clear all filters
                    </button>
                </div>
            )}
        </div>

        {/* Pagination Section */}
        {totalPages > 1 && (
            <div className='flex justify-center items-center gap-2 mt-10'>
                <button 
                    onClick={() => handlePageChange(1)}
                    className={`w-10 h-10 rounded-lg font-bold shadow-md transition-all ${currentPage === 1 ? 'bg-KITH-primary text-white' : 'bg-white text-gray-400 border border-gray-200 hover:bg-gray-50'}`}
                >1</button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => page !== 1)
                  .map(page => (
                    <button 
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-10 h-10 rounded-lg font-bold transition-all ${currentPage === page ? 'bg-KITH-primary text-white shadow-md' : 'bg-white text-gray-400 border border-gray-200 hover:bg-gray-50'}`}
                    >{page}</button>
                  ))
                }

                <button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className='w-10 h-10 rounded-lg border border-gray-200 text-gray-400 font-bold hover:bg-KITH-primary hover:text-white transition-all disabled:opacity-50 flex items-center justify-center'
                >
                    <i className='fi fi-br-angle-right mt-1'></i>
                </button>
            </div>
        )}
    </div>
  )
}

export default TourDisplayArea
